import { FinalTournamentView } from '../components/scoring/FinalTournamentView';
import { DeskTeamCards } from '../components/scoring/DeskTeamCards';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../components/AuthProvider';
import { checkedInToast, pushScoringToast } from '../components/common/admin-toasts';
import { EventDayBar } from '../components/scoring/EventDayBar';
import { useEventDayToggle } from '../hooks/useEventDayToggle';
import { useScoringTournament } from '../hooks/useScoringTournament';
import { ApiError, apiFetch } from '../lib/api';
import type { Arrival, CheckInStatus } from '../lib/check-in-queue';
import { shooterMatchesQuery, writeEventDayTab } from '../lib/event-day';
import { scoringTitle } from '../lib/page-title';
import { scoringListPath } from '../lib/scoring-routes';
import { RIFLE_TOURNAMENT_SLUG, SCORING_POLL_MS, normalizeBibPrefix } from '../lib/shooting';
import { scoringFieldClass } from './ScoringAdminPage';
import type { TeamQueue } from '../lib/team-check-in';

type ViewStatus = 'not_checked_in' | CheckInStatus;

interface CheckInShooter {
  shooterId: string;
  name: string;
  bib: string | null;
  group: 1 | 2 | 3 | null;
  slotLabel: string | null;
  status: ViewStatus;
  checkedInAt: string | null;
  canUndoCheckIn?: boolean;
  lane: number | null;
  arrival: Arrival | null;
  queuePlace: number | null;
  teamId: string | null;
  teamName: string | null;
  teamCheckedIn: number;
  teamTotal: number;
}

interface CheckInLane {
  lane: number;
  shooterId: string | null;
  name: string | null;
  bib: string | null;
  status: 'on_lane' | 'shooting' | null;
}

interface CheckInWaiting {
  shooterId: string;
  name: string;
  bib: string | null;
  group: 1 | 2 | 3 | null;
  slotLabel: string | null;
  arrival: Arrival;
  checkedInAt: string;
}

interface CheckInState {
  tournamentId: string;
  eventDay: string;
  graceMinutes: number;
  queuePanel: boolean;
  queueAutomatic?: boolean;
  slots: Record<'1' | '2' | '3', string>;
  shooters: CheckInShooter[];
  lanes: CheckInLane[];
  waiting: CheckInWaiting[];
  teams: TeamQueue;
  scoresEntered: number;
}

const primaryButton =
  'inline-flex min-h-12 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-accent)] px-4 text-base font-semibold text-white disabled:opacity-50';
const quietButton =
  'inline-flex min-h-11 items-center justify-center rounded-[var(--radius-md)] border border-[var(--color-border-strong)] px-3 text-sm font-medium text-[var(--color-text-primary)] disabled:opacity-50';

/** Desk and caller for the rifle event day. Admins only. The public board stays unchanged until the queue panel is on. */
export function CheckInPage({ mode }: { mode: 'desk' | 'caller' }) {
  const { isAdmin: admin, isOrganizer } = useAuth();
  const { slug = '' } = useParams();
  const isAdmin = admin || Boolean(isOrganizer && slug === RIFLE_TOURNAMENT_SLUG);
  const { detail, error: tournamentError, setDetail } = useScoringTournament(slug);
  const { eventDay, busy: eventDayBusy, error: eventDayError, toggle: toggleEventDay } = useEventDayToggle(detail, setDetail);
  const tournamentId = detail?.tournament.id;
  const [data, setData] = useState<CheckInState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [callerRound, setCallerRound] = useState<1 | 2>(1);
  const ticket = useRef(0);
  const readsInFlight = useRef(0);
  const mutationsInFlight = useRef(0);

  const apply = useCallback((id: number, next: CheckInState) => {
    if (id !== ticket.current) return;
    setData(next);
    setError(null);
  }, []);

  const load = useCallback(async (background = false) => {
    // Timer refreshes must not discard a slow read or a confirmed action response.
    if (!tournamentId || (background && (readsInFlight.current > 0 || mutationsInFlight.current > 0))) return;
    const id = ++ticket.current;
    readsInFlight.current++;
    try {
      const path = mode === 'caller' ? `caller?round=${callerRound}` : 'check-in';
      const body = await apiFetch<CheckInState>(`/api/admin/shooting/tournaments/${tournamentId}/${path}`);
      apply(id, body);
    } catch (err) {
      if (id !== ticket.current) return;
      setError(err instanceof ApiError ? err.message : 'The check-in list could not be loaded.');
    } finally {
      readsInFlight.current--;
    }
  }, [apply, tournamentId, mode, callerRound]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!tournamentId) return;
    const timer = window.setInterval(() => void load(true), SCORING_POLL_MS);
    return () => window.clearInterval(timer);
  }, [load, tournamentId]);

  useEffect(() => {
    document.title = scoringTitle(mode === 'caller' ? 'Caller' : 'Check in', detail?.tournament.title);
  }, [detail?.tournament.title, mode]);

  useEffect(() => {
    writeEventDayTab(slug, mode === 'caller' ? 'caller' : 'check-in');
  }, [mode, slug]);

  async function mutate(path: string, init: RequestInit, busyKey: string) {
    if (!tournamentId) return null;
    mutationsInFlight.current++;
    const id = ++ticket.current;
    setBusy(busyKey);
    setError(null);
    try {
      const body = await apiFetch<{ state: CheckInState; name?: string; checkInId?: string }>(path, init);
      apply(id, body.state);
      return body;
    } catch (err) {
      if (id === ticket.current && mode === 'caller' && err instanceof ApiError && err.status === 409) {
        try {
          const fresh = await apiFetch<CheckInState>(`/api/admin/shooting/tournaments/${tournamentId}/caller?round=${callerRound}`);
          apply(id, fresh);
        } catch {
          // Keep the conflict message if the refresh also fails; polling can retry.
        }
      }
      if (id === ticket.current) setError(err instanceof ApiError ? err.message : 'That did not save.');
      return null;
    } finally {
      mutationsInFlight.current--;
      if (mutationsInFlight.current === 0) setBusy(null);
    }
  }

  async function checkIn(shooter: Pick<CheckInShooter, 'shooterId' | 'name'>) {
    const body = await mutate(
      `/api/admin/shooting/tournaments/${tournamentId}/check-in`,
      { method: 'POST', body: JSON.stringify({ shooterId: shooter.shooterId }) },
      shooter.shooterId,
    );
    if (!body) return;
    const attended = body.state.shooters.find((row) => row.shooterId === shooter.shooterId);
    const canUndo = attended?.canUndoCheckIn ?? attended?.status === 'waiting';
    pushScoringToast(checkedInToast(body.name ?? shooter.name), canUndo ? {
      label: 'Undo',
      run: () => {
        void mutate(
          `/api/admin/shooting/tournaments/${tournamentId}/check-in/${shooter.shooterId}/undo`,
          { method: 'POST', body: JSON.stringify({ checkInId: body.checkInId }) },
          `undo-${shooter.shooterId}`,
        );
      },
    } : undefined, true);
  }

  const matches = useMemo(() => {
    if (!query.trim() || !data) return [];
    const prefix = normalizeBibPrefix(detail?.bibPrefix);
    return data.shooters.filter((shooter) => shooterMatchesQuery(shooter, query, prefix));
  }, [data, detail?.bibPrefix, query]);

  const checkedIn = useMemo(() => {
    const rows = (data?.shooters ?? []).filter((shooter) => shooter.checkedInAt != null);
    return rows.sort((a, b) => (b.checkedInAt ?? '').localeCompare(a.checkedInAt ?? ''));
  }, [data]);

  const scoresEntered = data?.scoresEntered ?? 0;
  const rosterTotal = data?.shooters.length ?? (mode === 'caller' && callerRound === 2 ? 0 : detail?.shooters.length ?? 0);

  if (!isAdmin && detail?.tournament.status === 'final') return <FinalTournamentView detail={detail} />;

  return (
    <Layout>
      <div className={mode === 'desk' ? 'mx-auto max-w-4xl space-y-4' : 'mx-auto max-w-lg space-y-4'}>
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-sm text-[var(--color-text-tertiary)]">
              <Link to={scoringListPath(slug)} className="underline-offset-2 hover:underline">
                Scoring
              </Link>
            </p>
            <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">{mode === 'caller' ? 'Caller' : 'Check in'}</h1>
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{detail?.tournament.title ?? 'Rifle shooting'}</p>
          </div>
        </header>

        <EventDayBar
          slug={slug}
          place={mode === 'caller' ? 'caller' : 'check-in'}
          isAdmin={isAdmin}
          eventDay={eventDay}
          toggleBusy={eventDayBusy}
          onToggle={toggleEventDay}
          toggleError={eventDayError}
          counts={{
            checkedIn: data ? checkedIn.length : null,
            total: rosterTotal,
            shootingNow: data ? data.lanes.filter((lane) => lane.shooterId).length : null,
            scoresEntered,
            waiting: data ? data.waiting.length : null,
          }}
        />

        {tournamentError || error ? (
          <p className="rounded-[var(--radius-md)] bg-[var(--color-error-subtle)] px-4 py-3 text-sm text-[var(--color-error-text)]" role="alert">
            {error ?? tournamentError}
          </p>
        ) : null}

        {mode === 'desk' ? (
          <DeskTeamCards
            shooters={data?.shooters ?? []}
            queue={data?.teams ?? {waiting:[],active:[],incomplete:[],latecomers:[]}}
            query={query}
            onQuery={setQuery}
            matches={matches}
            busy={busy}
            onCheckIn={(shooter) => void checkIn(shooter)}
            onUndo={(shooter) =>
              void mutate(
                `/api/admin/shooting/tournaments/${tournamentId}/check-in/${shooter.shooterId}/undo`,
                { method: 'POST' },
                `undo-${shooter.shooterId}`,
              )
            }
          />
        ) : (
          <><div className="flex gap-2" aria-label="Caller round">
            {([1, 2] as const).map((round) => <button key={round} type="button" aria-pressed={callerRound === round} className={callerRound === round ? primaryButton : quietButton} onClick={() => { setData(null); setCallerRound(round); }}>Round {round}</button>)}
          </div><Caller
            data={data}
            busy={busy}
            onLateAction={(shooterId, action) => void mutate(`/api/admin/shooting/tournaments/${tournamentId}/caller/late/${shooterId}/${action}?round=${callerRound}`, { method: 'POST' }, `${action}-${shooterId}`)}
            onTeamAction={(teamId, action) => void mutate(`/api/admin/shooting/tournaments/${tournamentId}/caller/teams/${teamId}/${action}?round=${callerRound}`, { method: 'POST' }, `${action}-${teamId}`)}
          /></>
        )}

        {data && mode === 'desk' ? (
          <Settings
            data={data}
            busy={busy}
            onSave={(body, key) =>
              void mutate(
                `/api/admin/shooting/tournaments/${tournamentId}/check-in/settings`,
                { method: 'PATCH', body: JSON.stringify(body) },
                key,
              )
            }
          />
        ) : null}
      </div>
    </Layout>
  );
}

function Caller({ data, busy, onTeamAction, onLateAction }: {
  data: CheckInState | null;
  busy: string | null;
  onTeamAction: (teamId: string, action: 'next' | 'up' | 'down' | 'call' | 'done' | 'undo' | 'reopen') => void;
  onLateAction: (shooterId: string, action: 'call' | 'done') => void;
}) {
  const active = data?.teams.active[0];
  const waiting = data?.teams.waiting ?? [];
  const occupied = data?.lanes.filter((lane) => lane.shooterId) ?? [];
  const attendance = (team: TeamQueue['waiting'][number]) => `${team.checkedIn} of ${team.total} checked in${team.complete ? '' : ` · ${team.total - team.checkedIn} missing`}`;

  const names = (team: TeamQueue['waiting'][number]) => team.members.map((member) => `${member.name}${member.status === 'not_checked_in' ? ' (not checked in)' : ''}`).join(', ');
  return (
    <>
      <section aria-label="Now shooting" className="space-y-2 rounded-[var(--radius-lg)] border border-[var(--color-border)] p-4">
        <h2 className="text-sm font-semibold uppercase">Now shooting</h2>
        {active ? <><p className="text-xl font-semibold">{active.name}</p><p>{names(active)}</p><p className={active.complete ? 'text-sm' : 'text-sm font-semibold text-amber-700'}>{attendance(active)}</p>
          <div className="flex flex-wrap gap-2"><button type="button" className={primaryButton} disabled={busy != null} onClick={() => { if (window.confirm(callerCompletionMessage(active))) onTeamAction(active.teamId, 'done'); }}>Team done</button>
          <button type="button" className={quietButton} disabled={busy != null} onClick={() => { if (window.confirm(`Move ${active.name} back to Following?`)) onTeamAction(active.teamId, 'undo'); }}>Undo / move back</button></div></> : <p>No team shooting now.</p>}
      </section>
      <section aria-label="Up next" className="space-y-2 rounded-[var(--radius-lg)] border border-[var(--color-border)] p-4">
        <h2 className="text-sm font-semibold uppercase">Up next</h2>
        {waiting[0] ? <><p className="text-xl font-semibold">{waiting[0].name}</p><p>{names(waiting[0])}</p><p role={waiting[0].complete ? undefined : 'status'} className={waiting[0].complete ? 'text-sm' : 'text-sm font-semibold text-amber-700'}>{attendance(waiting[0])}</p>
          <button type="button" className={primaryButton} disabled={busy != null || Boolean(active) || occupied.length > 0 || waiting[0].checkedIn === 0} onClick={() => { const team = waiting[0]!; if (team.complete || window.confirm(`${attendance(team)}. Send only checked-in members of ${team.name}?`)) onTeamAction(team.teamId, 'call'); }}>Send to lanes</button></> : <p>No team waiting.</p>}
      </section>
      {occupied.length ? <section aria-label="Occupied lanes" className="space-y-2"><h2 className="text-sm font-semibold uppercase">Occupied lanes</h2>
        {occupied.map((lane) => <div key={lane.lane} className="flex items-center justify-between gap-2 rounded border border-[var(--color-border)] p-3">
          <p>Lane {lane.lane} · {lane.name}</p><button type="button" className={quietButton} disabled={busy != null} onClick={() => { if (window.confirm(`Mark ${lane.name} done and free lane ${lane.lane}?`)) onLateAction(lane.shooterId!, 'done'); }}>Shooter done</button>
        </div>)}
      </section> : null}
      {data?.teams.latecomers.length ? <section aria-label="Late arrivals" className="space-y-2"><h2 className="text-sm font-semibold uppercase">Late arrivals</h2>
        {data.teams.latecomers.map((person) => <div key={person.shooterId} className="rounded border border-[var(--color-border)] p-3"><p>{person.name} · {data.shooters.find((row) => row.shooterId === person.shooterId)?.teamName}</p>
          <button type="button" className={quietButton} disabled={busy != null || occupied.length >= 6} onClick={() => onLateAction(person.shooterId, 'call')}>Send to free lane</button></div>)}
      </section> : null}
      {data?.teams.completed?.length ? <section aria-label="Completed teams" className="space-y-2"><h2 className="text-sm font-semibold uppercase">Completed teams</h2>
        {data.teams.completed.map((team) => <div key={team.teamId} className="rounded border border-[var(--color-border)] p-3"><p>{team.name}</p>
          <button type="button" className={quietButton} disabled={busy != null || occupied.length > 0 || Boolean(active)} onClick={() => { if (window.confirm(`Undo completion and return ${team.name} to its lanes?`)) onTeamAction(team.teamId, 'reopen'); }}>Undo team done</button></div>)}
      </section> : null}
      <section aria-label="Following" className="space-y-2"><h2 className="text-sm font-semibold uppercase">Following</h2>
        {waiting.slice(1).map((team, index) => <div key={team.teamId} className="rounded-[var(--radius-lg)] border border-[var(--color-border)] p-3">
          <h3 className="font-semibold">{team.name} · {team.checkedIn}/{team.total} checked in</h3><p className="text-sm">{names(team)}</p>
          <div className="mt-2 flex flex-wrap gap-2"><button type="button" className={quietButton} disabled={busy != null} onClick={() => onTeamAction(team.teamId, 'next')}>Set as Up next</button>
            <button type="button" className={quietButton} disabled={busy != null} onClick={() => onTeamAction(team.teamId, 'up')}>Move up</button>
            <button type="button" className={quietButton} disabled={busy != null || index === waiting.length - 2} onClick={() => onTeamAction(team.teamId, 'down')}>Move down</button></div>
        </div>)}
      </section>
    </>
  );
}

function Settings({
  data,
  busy,
  onSave,
}: {
  data: CheckInState;
  busy: string | null;
  onSave: (body: { queuePanel?: boolean }, key: string) => void;
}) {
  return (
    <details className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-1)] px-4 py-3">
      <summary className="cursor-pointer text-sm font-semibold text-[var(--color-text-primary)]">Queue settings</summary>
      <div className="mt-3 space-y-4">
        <button
          type="button"
          role="switch"
          aria-checked={data.queuePanel}
          disabled={busy != null || data.queueAutomatic}
          onClick={() => onSave({ queuePanel: !data.queuePanel }, 'panel')}
          className={`${quietButton} w-full justify-between`}
        >
          <span>Show the queue on the big screen and live board</span>
          <span className="font-semibold">{data.queuePanel ? 'On' : 'Off'}</span>
        </button>
        {data.queueAutomatic ? <p className="text-sm text-[var(--color-text-secondary)]">The queue stays on while rifle teams are set up.</p> : null}
      </div>
    </details>
  );
}

export function callerCompletionMessage(team: { name: string }): string {
  return `Mark ${team.name} done and free its lanes?`;
}
